import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Play, Square, Loader2, ArrowRight } from "lucide-react";
import { engine } from "@/audio/engine";
import Visualizer from "@/studio/Visualizer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SharePlayer() {
  const { shareId } = useParams();
  const [status, setStatus] = useState("loading"); // loading | ready | notfound | error
  const [name, setName] = useState("");
  const [cover, setCover] = useState("");
  const [playing, setPlaying] = useState(false);
  const loadedRef = useRef(false);

  useEffect(() => {
    axios
      .get(`${API}/shared/${shareId}`)
      .then((res) => {
        setName(res.data.name || "Untitled");
        const coverUrl = res.data.cover_url ? `${process.env.REACT_APP_BACKEND_URL}${res.data.cover_url}` : "";
        setCover(coverUrl);
        document.title = `${res.data.name || "Untitled"} · BeatMXR`;
        if (coverUrl) {
          const setMeta = (prop, val) => {
            let el = document.querySelector(`meta[property="${prop}"]`);
            if (!el) {
              el = document.createElement("meta");
              el.setAttribute("property", prop);
              document.head.appendChild(el);
            }
            el.setAttribute("content", val);
          };
          setMeta("og:title", `${res.data.name} · BeatMXR`);
          setMeta("og:description", "Listen to this beat made on BeatMXR.");
          setMeta("og:image", coverUrl);
          setMeta("og:type", "music.song");
        }
        engine.ensureStarted();
        engine.load(res.data.data);
        if (engine.ctx && engine.ctx.state === "running") engine.ctx.suspend();
        loadedRef.current = true;
        setStatus("ready");
      })
      .catch((e) => setStatus(e.response?.status === 404 ? "notfound" : "error"));
    return () => {
      try {
        engine.stop();
      } catch (_) {}
    };
  }, [shareId]);

  const toggle = () => {
    if (!loadedRef.current) return;
    engine.ensureStarted();
    if (engine.ctx && engine.ctx.state === "suspended") engine.ctx.resume();
    if (playing) {
      engine.stop();
      setPlaying(false);
    } else {
      engine.play();
      setPlaying(true);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col items-center justify-center grain px-6" data-testid="share-player">
      <img src="/wordmark_stacked.png" alt="BeatMXR" className="w-64 mb-8 object-contain" />

      {status === "loading" && (
        <div className="flex items-center gap-2 text-neutral-400">
          <Loader2 className="animate-spin" size={18} /> Loading song…
        </div>
      )}

      {status === "notfound" && (
        <p className="text-neutral-400" data-testid="share-notfound">This song link is invalid or was removed.</p>
      )}
      {status === "error" && (
        <p className="text-neutral-400">Couldn't load this song. Please try again.</p>
      )}

      {status === "ready" && (
        <div className="w-full max-w-xl flex flex-col items-center gap-6">
          {cover && (
            <img
              data-testid="share-cover"
              src={cover}
              alt={name}
              className="w-48 h-48 md:w-56 md:h-56 object-cover rounded-xl border border-[#262626]"
              style={{ boxShadow: "0 0 40px rgba(168,85,247,0.35)" }}
            />
          )}
          <div className="text-center">
            <div className="hw-label text-[10px] text-neutral-500">Now playing</div>
            <h1 className="font-display font-black text-3xl tracking-wide mt-1" data-testid="share-song-name">{name}</h1>
          </div>

          <div className="w-full rounded-lg border border-[#262626] bg-[#0d0d0d] p-4">
            <Visualizer height={120} bars={56} />
          </div>

          <button
            data-testid="share-play-btn"
            onClick={toggle}
            className="w-20 h-20 rounded-full flex items-center justify-center text-black transition-transform hover:scale-105"
            style={{ background: "linear-gradient(120deg,#00e5ff,#a855f7)", boxShadow: "0 0 30px rgba(0,229,255,0.45)" }}
          >
            {playing ? <Square size={30} fill="black" /> : <Play size={30} fill="black" className="ml-1" />}
          </button>

          <Link
            to="/studio"
            data-testid="share-cta"
            className="flex items-center gap-2 mt-2 px-5 py-3 rounded-sm border border-[#00e5ff] text-[#00e5ff] font-bold text-sm hover:bg-[#00e5ff]/10 transition-colors"
          >
            Make your own beat on BeatMXR <ArrowRight size={16} />
          </Link>
        </div>
      )}
    </div>
  );
}
